import React, { useState, useEffect } from 'react';
import { ReactComponent as BackIcon } from "../../../../assets/ChatbotReportsSvgs/BackButtonChatbotIcon.svg";
import { ReactComponent as CautionIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/CautionResponseChatbotReportsIcon.svg";
import { ReactComponent as GrowthMetricsIcon } from "../../../../assets/ChatbotReportsSvgs/MetricsSvgs/GrowthMetricsIcon.svg";
import './style.scss';
import { KpiCard } from '../../../../interface/ReportsChatbotInterface';

const PAGE_SIZE = 5;

/** Strips trailing dev/spec annotations like "Net Sales (hero tile, drillable)" that leak through from the API title. */
const sanitizeCardTitle = (title: string): string => title.replace(/\s*\([^)]*\)\s*$/, '').trim();

type DataTableCard = Extract<KpiCard, { type: 'data_table' }>;

/** Guards against API rows that send an object/array for a cell instead of a scalar. */
const formatCellValue = (value: unknown): string => {
    if (value === undefined || value === null || value === '') return '–';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

interface KpiCardBubbleProps {
    sender?: 'user' | 'ai';
    card: KpiCard;
    isFailure?: boolean;
    apiFailureMessage?: string;
    onRetry?: () => void;
}

/** Paginated table for the data_table card shape (own page state, mirrors DetailTableCard). */
const KpiDataTableCard: React.FC<{ card: DataTableCard }> = ({ card }) => {
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [card.rows]);

    const totalPages = Math.max(1, Math.ceil(card.rows.length / PAGE_SIZE));
    const start = (currentPage - 1) * PAGE_SIZE;
    const pagedRows = card.rows.slice(start, start + PAGE_SIZE);
    const showingFrom = start + 1;
    const showingTo = Math.min(currentPage * PAGE_SIZE, card.rows.length);
    const total = card.rows.length;

    return (
        <div className="kpi-card-data-table">
            <div className="kpi-card-data-table-title">{sanitizeCardTitle(card.title)}</div>
            <div className="kpi-card-table-wrapper">
                <table className="kpi-card-table">
                    <thead>
                        <tr className="kpi-card-table-header">
                            {card.columns.map((col) => (
                                <th key={col.key} className="kpi-card-table-header-cell">
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pagedRows.map((row, idx) => (
                            <tr className="kpi-card-table-row" key={idx}>
                                {card.columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={`kpi-card-table-cell ${typeof row[col.key] === 'number' ? 'kpi-card-table-cell--numeric' : ''}`}
                                    >
                                        {formatCellValue(row[col.key])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="kpi-card-pagination">
                    <span className="kpi-card-pagination__info">
                        {showingFrom}–{showingTo} of {total}
                    </span>
                    <div className="kpi-card-pagination__controls">
                        <button
                            className="kpi-card-pagination__btn"
                            onClick={() => setCurrentPage((p) => p - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <BackIcon className="kpi-card-pagination__icon" />
                        </button>
                        <span className="kpi-card-pagination__page">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            className="kpi-card-pagination__btn"
                            onClick={() => setCurrentPage((p) => p + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <BackIcon className="kpi-card-pagination__icon kpi-card-pagination__icon--next" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Renders the pre-normalized `card` field from /generate_kpi_data.
 * Switches on card.type only (comparison / table / data_table / single_value) —
 * never infers the shape from raw_results/results. card === null renders nothing,
 * the caller should still render the insight text bubble in that case.
 */
const KpiCardBubble: React.FC<KpiCardBubbleProps> = ({
    sender: senderProp = 'ai',
    card,
    isFailure = false,
    apiFailureMessage = 'Unable to retrieve at the moment',
    onRetry,
}) => {
    if (isFailure) {
        return (
            <div className={`kpi-card-message-row ${senderProp}-message`}>
                <div className="kpi-card-message-bubble">
                    <div className="kpi-card-failure-state">
                        <CautionIconChatbotReportsIcon className="kpi-card-caution-icon" />
                        <span className="kpi-card-failure-text">{apiFailureMessage}</span>
                        {onRetry && (
                            <button className="kpi-card-retry-btn" type="button" onClick={onRetry}>
                                Retry
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!card) return null;

    return (
        <div className={`kpi-card-message-row ${senderProp}-message`}>
            <div className="kpi-card-message-bubble">
                {card.type === 'data_table' ? (
                    <KpiDataTableCard card={card} />
                ) : (
                    <div className="kpi-card-content">
                        <div className="kpi-card-title">{sanitizeCardTitle(card.title)}</div>

                        {card.type === 'single_value' ? (
                            <div className="kpi-card-single-value">{card.value}</div>
                        ) : (
                            card.rows.map((row, i) => (
                                <div className="kpi-card-row" key={`${row.label}-${i}`}>
                                    <span className="kpi-card-row-label">{row.label}</span>
                                    <span className="kpi-card-row-value">{formatCellValue(row.value)}</span>
                                </div>
                            ))
                        )}

                        {card.type === 'comparison' && card.delta && (
                            <div className={`kpi-card-delta ${card.delta.direction === 'up' ? 'kpi-card-delta-up' : 'kpi-card-delta-down'}`}>
                                <GrowthMetricsIcon className="kpi-card-delta-icon" />
                                <span className="kpi-card-delta-text">{card.delta.text}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KpiCardBubble;
